import React, { useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Download, Loader2, Mail, ShieldCheck, FileText, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STEPS = { EMAIL: 'email', OTP: 'otp', REPORTS: 'reports' };

export default function ReportHistory() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/report-history/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('OTP sent! Check your inbox.');
        setStep(STEPS.OTP);
      } else {
        toast.error(data.detail || 'Failed to send OTP. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/report-history/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setReports(data.reports || []);
        setStep(STEPS.REPORTS);
      } else {
        toast.error(data.detail || 'Invalid OTP. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report) => {
    setDownloadingId(report.id);
    try {
      const res = await fetch(
        `${API_URL}/api/report-history/download/${report.id}?email=${encodeURIComponent(email)}`
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Brand_Strategy_Guide_${(report.company_name || 'Brand').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report downloaded!');
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-black py-16 px-4">
        <div className="max-w-xl mx-auto">

          {/* Back */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-10 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>

          <h1 className="text-4xl font-extrabold text-white tracking-tight mb-2">Report History</h1>
          <p className="text-gray-400 text-sm mb-10">Re-download your purchased Brand Strategy Guides.</p>

          {/* STEP 1: Email */}
          {step === STEPS.EMAIL && (
            <form onSubmit={handleRequestOTP} data-testid="email-step">
              <div className="bg-zinc-900 border border-zinc-800 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0a0a0a] flex items-center justify-center">
                    <Mail className="h-5 w-5 text-[#f5f5f5]" />
                  </div>
                  <div>
                    <div className="text-white font-bold">Enter your email</div>
                    <div className="text-[#9ca3af] text-xs">We'll send a verification code</div>
                  </div>
                </div>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-black border-zinc-700 text-white placeholder:text-zinc-600 h-12 mb-4"
                  data-testid="email-input"
                />
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-12 bg-[#0a0a0a] text-[#f5f5f5] font-bold hover:bg-[#1a1a1a] text-sm tracking-widest uppercase"
                  data-testid="send-otp-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Verification Code'}
                </Button>
              </div>
            </form>
          )}

          {/* STEP 2: OTP */}
          {step === STEPS.OTP && (
            <form onSubmit={handleVerifyOTP} data-testid="otp-step">
              <div className="bg-zinc-900 border border-zinc-800 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#0a0a0a] flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-[#f5f5f5]" />
                  </div>
                  <div>
                    <div className="text-white font-bold">Enter verification code</div>
                    <div className="text-[#9ca3af] text-xs">Sent to {email}</div>
                  </div>
                </div>
                <Input
                  type="text"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="bg-black border-zinc-700 text-white placeholder:text-zinc-600 h-12 mb-4 tracking-widest text-center text-lg font-bold"
                  data-testid="otp-input"
                />
                <Button
                  type="submit"
                  disabled={loading || otp.length < 4}
                  className="w-full h-12 bg-[#0a0a0a] text-[#f5f5f5] font-bold hover:bg-[#1a1a1a] text-sm tracking-widest uppercase mb-3"
                  data-testid="verify-otp-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & View Reports'}
                </Button>
                <button
                  type="button"
                  onClick={() => setStep(STEPS.EMAIL)}
                  className="w-full text-center text-[#9ca3af] text-xs hover:text-white transition-colors"
                >
                  Change email
                </button>
              </div>
            </form>
          )}

          {/* STEP 3: Reports list */}
          {step === STEPS.REPORTS && (
            <div data-testid="reports-step">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-white font-bold">{email}</div>
                  <div className="text-[#9ca3af] text-xs">{reports.length} report{reports.length !== 1 ? 's' : ''} found</div>
                </div>
                <button
                  onClick={() => { setStep(STEPS.EMAIL); setOtp(''); setReports([]); }}
                  className="text-[#9ca3af] text-xs hover:text-white transition-colors"
                >
                  Switch account
                </button>
              </div>

              {reports.length === 0 ? (
                <div className="bg-zinc-900 border border-zinc-800 p-12 text-center">
                  <FileText className="h-10 w-10 text-zinc-600 mx-auto mb-4" />
                  <div className="text-gray-400 font-medium mb-2">No reports found</div>
                  <div className="text-[#a3a3a3] text-sm">No paid reports found for this email address.</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div
                      key={report.id}
                      className="bg-zinc-900 border border-zinc-800 p-5 flex items-center justify-between gap-4"
                      data-testid={`report-item-${report.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-bold text-sm truncate">
                          {report.company_name || 'Your Brand'}
                        </div>
                        <div className="text-[#9ca3af] text-xs mt-1">
                          {report.primary_archetype}{report.secondary_archetype ? ` + ${report.secondary_archetype}` : ''} · {formatDate(report.created_at)}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownload(report)}
                        disabled={downloadingId === report.id}
                        className="bg-[#0a0a0a] text-[#f5f5f5] hover:bg-[#1a1a1a] font-bold text-xs h-9 px-4 shrink-0 tracking-wider uppercase"
                        data-testid={`download-report-${report.id}`}
                      >
                        {downloadingId === report.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Download className="h-3 w-3 mr-1.5" />
                            Download
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
